import moment from 'moment-timezone';
import i18n from './i18n';
import {DateFormats} from '@adsk/bim360-matrix-react-helpers/es/consts';

const defaultDateFormat = date => {
  if (!date._isAMomentObject) {
    date = moment(date);
  }

  return new Date().getFullYear() === date.year()
    ? DateFormats.SHORT_DATE_NO_YEAR_FORMAT
    : DateFormats.SHORT_DATE_FORMAT;
};

// For formatting into an ISO-8601 date string, sans time:
const ISO_DATE_FORMAT = 'YYYY-MM-DD';
export const toIsoDate = dateObjectOrString =>
  dateObjectOrString ? moment(dateObjectOrString).format(ISO_DATE_FORMAT) : null;

const formatStartDate = (isoPureDateString, isUtc = false, isIso = false) => {
  if (isoPureDateString) {
    const dateFunction = isUtc ? moment.utc : moment;
    return dateFunction(isoPureDateString).format(isIso ? ISO_DATE_FORMAT : undefined);
  }
  return '';
};

const formatEndDate = (isoPureDateString, isUtc = false, isIso = false) => {
  if (isoPureDateString) {
    const dateFunction = isUtc ? moment.utc : moment;
    return dateFunction(isoPureDateString).format(isIso ? ISO_DATE_FORMAT : undefined);
  }
  return '';
};
const formatStartDateTime = isoPureDateString => {
  if (isoPureDateString) {
    return moment(isoPureDateString)
      .startOf('day')
      .format();
  }
  return '';
};

const formatEndDateTime = isoPureDateString => {
  if (isoPureDateString) {
    return moment(isoPureDateString)
      .endOf('day')
      .format();
  }
  return '';
};

export const formatStartDateUtcWithTimeZone = isoPureDateString => {
  if (isoPureDateString) {
    return moment
      .tz(isoPureDateString, moment.tz.guess())
      .startOf('day')
      .utc()
      .format();
  }
  return null;
};

export const formatEndDateUtcWithTimeZone = isoPureDateString => {
  if (isoPureDateString) {
    return moment
      .tz(isoPureDateString, moment.tz.guess())
      .endOf('day')
      .utc()
      .format();
  }
  return null;
};
export const formatUtcDateRange = (isoPureDateStringStart, isoPureDateStringEnd, {isIso = true} = {}) =>
  `${formatStartDate(isoPureDateStringStart, true, isIso)}...${formatEndDate(isoPureDateStringEnd, true, isIso)}`;

export const formatLocalDateTimeRange = (isoPureDateStringStart, isoPureDateStringEnd) =>
  `${formatStartDateTime(isoPureDateStringStart)}...${formatEndDateTime(isoPureDateStringEnd)}`;

// For formatting into an ISO-8601 date string, sans time:
const ISO_DATE_FORMAT_WITH_TIME = 'YYYY-MM-DD HH:mm:SS';
export const toIsoDateWithTime = dateObjectOrString =>
  dateObjectOrString ? moment(dateObjectOrString).format(ISO_DATE_FORMAT_WITH_TIME) : null;

export const toIsoEndDateWithTime = dateObjectOrString =>
  dateObjectOrString
    ? moment(dateObjectOrString)
      .add(1, 'days')
      .format(ISO_DATE_FORMAT_WITH_TIME)
    : null;

// For converting ISO-8601 date string into a native JavaScript Date Object:
// (Returns null if utcIsoDateString is falsey)
// toLocalDate accepts: 'YYYY-MM-DD' || 'YYYY-MM-DD<time><tz>'
export const toLocalDate = utcIsoDateString => {
  if (!utcIsoDateString) {
    return null;
  }
  // If utcIsoDateString has a time (e.g.: '2016-06-16T00:00:00.000Z'),
  // it must be converted to a pure-date string, *without* time (e.g.: '2016-06-16'):
  const pureDate = toIsoDate(moment.utc(utcIsoDateString));
  return moment(pureDate).toDate();
};

export const i18nShortDateFormat = () => moment.localeData().longDateFormat('L');
// en: 'MM/DD/YYYY'
// de: 'DD.MM.YYYY'
export const toUtcShortDate = utcIsoDateTimeString => moment.utc(utcIsoDateTimeString).format(i18nShortDateFormat());
export const toLocalShortDate = utcIsoDateTimeString => moment(utcIsoDateTimeString).format(i18nShortDateFormat());

export const i18nLongDateFormat = () => moment.localeData().longDateFormat('ll');
// en: 'MMM D, YYYY'
// de: 'D. MMM YYYY'
export const toUtcLongDate = utcIsoDateTimeString => moment.utc(utcIsoDateTimeString).format(i18nLongDateFormat());
export const toLocalLongDate = utcIsoDateTimeString => moment(utcIsoDateTimeString).format(i18nLongDateFormat());

export const i18nDateTimeFormat = () => moment.localeData().longDateFormat('lll');
// en: 'MMM D, YYYY h:mm A'
// de: 'D. MMM YYYY HH:mm'
// Displays as local browser time -- not UTC:
export const toLocalDateTime = utcIsoDateTimeString => moment(utcIsoDateTimeString).format(i18nDateTimeFormat());

// This returns a native JavaScript Date object:
export const now = () => moment().toDate();

// Checks if given date was already past:
export const isHistory = utcIsoDateTimeString => moment.utc(utcIsoDateTimeString).isBefore(moment());

// Check if a dueDate is late (less than 1 day ahead)
export const isLate = dueDate => {
  if (!dueDate) {
    return false;
  }

  const wrappedDueDate = moment(dueDate);
  const wrappedTomorrow = moment().add(1, 'days');
  const diffInMS = moment.duration(wrappedDueDate.diff(wrappedTomorrow));

  return diffInMS < 0;
};

const REPORT_DATE_FORMAT = 'MM-DD-YY';
const REPORT_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';
export const toReportFileNameDate = dateObjectOrString =>
  dateObjectOrString ? moment(dateObjectOrString).format(REPORT_DATE_FORMAT) : null;

export const toReportFileNameDateAndTime = dateObjectOrString =>
  dateObjectOrString ? moment(dateObjectOrString).format(REPORT_DATE_TIME_FORMAT) : null;

export const shortRelativeDateString = date => {
  const momentDate = moment(date).startOf('day');
  const today = moment().startOf('day');
  const dayDiff = momentDate.diff(today, 'days');

  switch (dayDiff) {
    case 1:
      return i18n.t('date.tomorrow');
    case 0:
      return i18n.t('date.today');
    case -1:
      return i18n.t('date.yesterday');
    default:
      return momentDate.format(defaultDateFormat(momentDate));
  }
};
export const fromDateSelectorDate = date => toIsoDate(moment([date.getFullYear(), date.getMonth(), date.getDate()]));

export const getIsoDateDaysFromNow = daysFromNow =>
  toIsoDate(
    moment()
      .startOf('day')
      .add(daysFromNow, 'days')
  );
